import { CartItems } from '@/hooks/CartContext';
import { DiscountStorageProps } from '@/types';
import {
    ComboCartItemAllocation,
    buildComboDisplaySplitsForCartLine,
    getComboDiscountParticipatingQuantityForCartItem,
} from './comboCartUtils';

interface ComboFlexPortion {
    comboPortion?: 'combo' | 'excess';
    comboDiscountQuantity?: number;
}

/**
 * Porção com combo ATIVO (kit completo dentro do max_combo): recebe o desconto de combo e não
 * acumula flex. Kit em andamento (comboDiscountQuantity === 0) e excedente (comboPortion==='excess')
 * ficam de fora e aplicam flex normalmente. O flex persistido da porção não é apagado — só deixa de
 * ser lido enquanto isto for true; volta a valer sozinho se o combo desativar.
 */
export const isActiveComboPortion = (portion: ComboFlexPortion | null | undefined): boolean =>
    portion?.comboPortion === 'combo' && (portion?.comboDiscountQuantity ?? 0) > 0;

/**
 * Mesma regra a partir das alocações de UMA instância — para checkout/payload, que já recebem
 * `allocations` pré-filtradas por `comboInstanceId` e não têm a "fatia" de exibição em mãos.
 */
export const isActiveComboPortionFromAllocations = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    allocations: ComboCartItemAllocation[] | undefined
): boolean =>
    isActiveComboPortion({
        comboPortion: 'combo',
        comboDiscountQuantity: getComboDiscountParticipatingQuantityForCartItem(item, allocations),
    });

/** Percentual de flex efetivo desta porção: 0 quando bloqueado por combo ativo. */
export const resolveEffectiveFlexPercent = (
    percent: number | null | undefined,
    portion: ComboFlexPortion | null | undefined
): number => (isActiveComboPortion(portion) ? 0 : Number(percent) || 0);

/**
 * Chave de escopo do flex para um card que representa a linha INTEIRA (sem split visual por
 * porção, ex.: ProductCard fora da tela de combos) — a mesma chave combo-aware que o carrinho usa
 * (`buildComboDisplaySplitsForCartLine`, ver cartProductCard/DiscountFlexAmountContext). Sem isso,
 * um card que sempre lê/grava pelo uid base nunca encontra o flex aplicado numa porção com combo
 * (kit ativo ou excedente) — e vice-versa, o carrinho nunca vê o que foi aplicado pelo uid base.
 *
 * Prioriza a fatia excedente (é a única onde flex normal ainda se aplica com combo parcialmente
 * ativo), depois a fatia sem combo nenhum (line comum), e só cai pra fatia de combo (ou fallback)
 * quando a linha inteira está alocada a kits ativos — nesse caso o flex nem é lido/exibido mesmo
 * (ver isActiveComboPortion), a chave aqui só existe pra não quebrar a leitura.
 */
export const resolveFlexDiscountScopeKey = (
    item: CartItems | undefined | null,
    allocations: ComboCartItemAllocation[] | undefined,
    fallbackKey: string
): string => {
    if (!item) return fallbackKey;

    const slices = buildComboDisplaySplitsForCartLine(item, allocations);
    const excessSlice = slices.find((slice) => slice.comboPortion === 'excess');
    const plainSlice = slices.find((slice) => !slice.comboPortion);

    return (excessSlice ?? plainSlice ?? slices[0])?.displayKey ?? fallbackKey;
};

export const isActiveFlexRecord = (
    record: (DiscountStorageProps & { percentual?: number; saldoAFaturar?: number }) | null | undefined
): boolean => {
    if (!record) return false;
    return (Number(record.percentual) || 0) !== 0 || (Number(record.saldoAFaturar) || 0) > 0;
};

export const collectFlexDiscountLookupKeys = (
    item: CartItems | undefined | null,
    allocations: ComboCartItemAllocation[] | undefined,
    fallbackKey: string
): string[] => {
    const scoped = resolveFlexDiscountScopeKey(item, allocations, fallbackKey);
    const keys: string[] = [];
    const add = (key?: string | null) => {
        if (key && !keys.includes(key)) keys.push(key);
    };

    add(scoped);
    add(fallbackKey);
    add(item?.uid);

    return keys;
};

export const pickLatestFlexRecord = <T extends { updatedAtSeq?: number }>(
    keys: string[],
    getRecord: (uid: string) => T | undefined
): { record: T | undefined; uid: string } => {
    let best: { record: T; uid: string } | undefined;

    for (const uid of keys) {
        const record = getRecord(uid);
        if (!record) continue;
        if (!best || (record.updatedAtSeq || 0) > (best.record.updatedAtSeq || 0)) {
            best = { record, uid };
        }
    }

    return best ?? { record: undefined, uid: keys[0] };
};

export const lookupFlexRecordWithFallback = <T,>(
    primaryUid: string,
    fallbackUid: string | undefined,
    getRecord: (uid: string) => T | undefined
): T | undefined => {
    const primary = getRecord(primaryUid);
    if (primary) return primary;
    if (fallbackUid && fallbackUid !== primaryUid) return getRecord(fallbackUid);
    return undefined;
};

export const shouldMigrateFlexToScopeKey = (
    fromKey: string,
    toKey: string,
    fromRecord: (DiscountStorageProps & { percentual?: number; saldoAFaturar?: number }) | undefined,
    toRecord: (DiscountStorageProps & { percentual?: number; saldoAFaturar?: number }) | undefined
): boolean => {
    if (!fromKey || !toKey || fromKey === toKey) return false;
    if (!isActiveFlexRecord(fromRecord)) return false;
    if (isActiveFlexRecord(toRecord)) return false;
    return true;
};
